import React, { useMemo } from "react";
import DOMPurify from "dompurify";
import styles from "./SanitizedServiceDescription.module.css";

// Keep aligned with the admin TipTap editor (StarterKit + Underline + Link)
// and the backend bleach allow-list. Heading levels are constrained to h2-h4
// because the editor only exposes those controls.
const ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "em",
  "u",
  "s",
  "code",
  "blockquote",
  "hr",
  "h2",
  "h3",
  "h4",
  "ul",
  "ol",
  "li",
  "a",
];
// target/rel are set by the afterSanitizeAttributes hook below; admin-authored
// values are ignored so we can enforce safe external-link behavior.
const ALLOWED_ATTR = ["href", "title"];

// Register a module-level hook (idempotent) that forces every anchor to open
// in a new tab with rel="noopener noreferrer". This closes the reverse-tabnabbing
// risk for descriptions that happen to include links.
let hookRegistered = false;
if (!hookRegistered) {
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A") {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
    }
  });
  hookRegistered = true;
}

interface SanitizedServiceDescriptionProps {
  html?: string | null;
  className?: string;
}

const SanitizedServiceDescription: React.FC<
  SanitizedServiceDescriptionProps
> = ({ html, className }) => {
  const clean = useMemo(() => {
    if (!html) return "";
    return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR });
  }, [html]);

  if (!clean) return null;
  const combined = [styles.description, className].filter(Boolean).join(" ");
  return (
    <div className={combined} dangerouslySetInnerHTML={{ __html: clean }} />
  );
};

export default SanitizedServiceDescription;
